/**
 * PIN API for User PINs / device access.
 * Endpoints: POST/PUT /pins, POST /pins/validate, GET /account/pins/me (see docs/user-pins-api-server-spec.md).
 */

import { apiFetch } from "@common/lib/api";
import { HTTPError } from "ky";

const MAX_RETRY_DISPLAY_SECONDS = 3600;

/**
 * Cooldown seconds for PIN validate 429 responses. Prefer JSON `retry_after_seconds` from the API,
 * then `Retry-After` header, then a conservative default.
 */
export async function parsePinRateLimitRetrySeconds(error: unknown): Promise<number> {
    const fallbackSeconds = 60;
    if (!(error instanceof HTTPError) || error.response.status !== 429) return fallbackSeconds;

    try {
        const body = (await error.response.clone().json()) as Record<string, unknown>;
        const raw = body.retry_after_seconds ?? body.retryAfterSeconds;
        const n = typeof raw === "number" ? raw : typeof raw === "string" ? parseInt(raw, 10) : NaN;
        if (Number.isFinite(n) && n > 0) {
            return Math.min(Math.ceil(n), MAX_RETRY_DISPLAY_SECONDS);
        }
    } catch {
        // Non-JSON or empty body
    }

    const retryAfter = error.response.headers.get("Retry-After");
    if (retryAfter != null) {
        const parsed = parseInt(retryAfter, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return Math.min(parsed, MAX_RETRY_DISPLAY_SECONDS);
        }
    }

    return fallbackSeconds;
}

const PINS_PATH = "pins";
const PINS_ME_PATH = "account/pins/me";

export interface CreatePinBody {
    pin: string;
    account_id: string;
}

export interface UpdatePinBody {
    account_id: string;
    current_pin?: string;
    password?: string;
    new_pin: string;
}

export interface ValidatePinBody {
    account_id: string;
    pin: string;
    device_id: string;
}

export interface ValidatePinResponse {
    user_id: string;
    /** New access token for the resolved user (for unlock or session switch). Matches backend field name. */
    access_token: string;
    /** New refresh token when backend issues a full session (optional; required for Supabase setSession). */
    refresh_token?: string;
}

export interface PinsMeResponse {
    has_pin: boolean;
}

/** Create PIN for the current user in the given account. */
export async function createPin(body: CreatePinBody): Promise<void> {
    await apiFetch<void>(PINS_PATH, {
        method: "POST",
        json: body,
    });
}

/** Update own PIN (current PIN or password re-auth path). */
export async function updatePin(body: UpdatePinBody): Promise<void> {
    await apiFetch<void>(PINS_PATH, {
        method: "PUT",
        json: body,
    });
}

/** Validate PIN for unlock / user switch. Returns resolved user_id and optional new session. */
export async function validatePin(body: ValidatePinBody): Promise<ValidatePinResponse> {
    const data = await apiFetch<ValidatePinResponse>("pins/validate", {
        method: "POST",
        json: body,
    });
    if (!data) throw new Error("PIN validation failed");
    return data;
}

/**
 * Validate PIN when the client has no session (e.g. after lock() invalidated it).
 * Sends POST pins/validate without Authorization. Backend must allow this for the unlock
 * flow and rate-limit by (account_id, device_id). Returns a fresh session for setSession.
 */
export async function validatePinForUnlock(body: ValidatePinBody): Promise<ValidatePinResponse> {
    const data = await apiFetch<ValidatePinResponse>("pins/validate", {
        method: "POST",
        skipAuth: true,
        json: body,
    });
    if (!data?.access_token) throw new Error("PIN validation failed");
    return data;
}

/** HTTP status when the pins/me endpoint is not yet implemented (don't block app). */
const NOT_IMPLEMENTED_STATUS = 404;

/**
 * Check if the current user has a PIN for the given account (GET /account/pins/me).
 * If the endpoint returns 404 (not implemented yet), we treat as "has PIN" so the app
 * does not block behind PIN creation; remove this once the backend exposes the endpoint.
 * All other errors are rethrown so React Query shows error state (fail-closed).
 */
export async function getCurrentUserHasPin(accountId: string): Promise<boolean> {
    try {
        const data = await apiFetch<PinsMeResponse>(PINS_ME_PATH, {
            method: "GET",
            searchParams: { account_id: accountId },
        });
        return data?.has_pin === true;
    } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === NOT_IMPLEMENTED_STATUS) return true;
        throw e;
    }
}
